import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingFluidMd,
	spacingFluidSm,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import dayjs from "dayjs"
import { useEffect } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { DatepickerField, SelectField } from "src/components"
import { useDateSettingsOutsideDealership } from "src/hooks"
import type { ExportTimeFrame, ExportTimeFrameFormValues } from "../common/exportTimeFrame"
import { getEarliestExportStartDate, getExportTimeFrameDates } from "../common/exportTimeFrame"
import { marketExportMessages as messages } from "../common/marketExport.messages"

interface MarketExportTimeFrameFieldsProps {
	today: Date
}

export const MarketExportTimeFrameFields = ({ today }: MarketExportTimeFrameFieldsProps) => {
	const { formatDate, formatMessage } = useIntl()
	const { dateFormat, locale } = useDateSettingsOutsideDealership()
	const { control, setValue } = useFormContext<ExportTimeFrameFormValues>()
	const [timeFrame, startDate, endDate] = useWatch({ control, name: ["timeFrame", "startDate", "endDate"] })
	const isCustomSelection = timeFrame === "customSelection"

	useEffect(() => {
		if (timeFrame !== "customSelection") {
			const dates = getExportTimeFrameDates(timeFrame, today)
			setValue("startDate", dates.startDate)
			setValue("endDate", dates.endDate)
		}
	}, [timeFrame, setValue, today])

	const options: { label: string; value: ExportTimeFrame }[] = [
		{ label: formatMessage(messages.currentMonth), value: "currentMonth" },
		{ label: formatMessage(messages.lastMonth), value: "lastMonth" },
		{ label: formatMessage(messages.customSelection), value: "customSelection" },
	]
	const formatPeriodDate = (isoDate: string) =>
		formatDate(dayjs(isoDate).toDate(), { day: "2-digit", month: "2-digit", year: "numeric" })

	return (
		<Container>
			<Fields>
				<SelectField
					name="timeFrame"
					label={formatMessage(messages.timeFrame)}
					options={options}
					required
					shouldUnregister={false}
				/>
				{isCustomSelection && (
					<>
						<DatepickerField
							name="startDate"
							dateFormat={dateFormat}
							locale={locale}
							label={formatMessage(messages.startDate)}
							labelTooltip={formatMessage(messages.startDateHint)}
							minDate={getEarliestExportStartDate(today)}
							required
						/>
						<DatepickerField
							name="endDate"
							dateFormat={dateFormat}
							locale={locale}
							label={formatMessage(messages.endDate)}
							required
						/>
					</>
				)}
			</Fields>
			{!isCustomSelection && (
				<PText>
					<FormattedMessage
						{...messages.exportPeriod}
						values={{ endDate: formatPeriodDate(endDate), startDate: formatPeriodDate(startDate) }}
					/>
				</PText>
			)}
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
`

const Fields = styled.div`
	display: grid;
	gap: ${spacingFluidSm} ${spacingFluidMd};

	@media (min-width: ${breakpoint.s}px) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
`
